/**
 * 持仓探测（EDG-561 fe-spec §2.1 / §2.3）。
 *
 * 复用现有 `GET /financial/records?type=POSITION_ASSET&payload={"limit":1}`：`rows` 非空即有持仓。
 * 失败契约：任何异常 / 超时一律按「无持仓」，**不抛错** —— 入口分流据此开引导弹窗、登录后落 SC-03，
 * 所以宁可多问一次也不能把用户卡在探测上（fe-spec §3.6、后端设计 GAP-8）。
 */

export const HOLDINGS_PROBE_TIMEOUT_MS = 3_000;

/**
 * 上传 / 连券商成功后的这段时间内，探测读空多半是搜索索引还没追上（`/financial/records` 走多元索引），
 * 不是真的没持仓：先等 {@link HOLDINGS_RETRY_DELAY_MS} 再问一次，之后才按无持仓分流（review #122）。
 */
export const HOLDINGS_RECENT_CHANGE_WINDOW_MS = 10_000;
export const HOLDINGS_RETRY_DELAY_MS = 1_500;

/** 只声明本文件用到的最小形状，便于测试注入；`MemberClient` 满足它。 */
export interface HoldingsProbeClient {
  financial: {
    listFinancialRecords: (query: {
      type: "POSITION_ASSET";
      payload?: string;
    }) => Promise<{ data?: { data?: { rows?: unknown[] } } } | unknown>;
  };
}

const HOLDINGS_QUERY = {
  type: "POSITION_ASSET" as const,
  payload: JSON.stringify({ limit: 1 }),
};

const extractRows = (res: unknown): unknown[] | undefined => {
  const body = (res as { data?: { data?: { rows?: unknown[] } } } | undefined)
    ?.data;
  return body?.data?.rows;
};

/** 单次查询：rows 非空 → true；任何异常 / 超时 → false。 */
export async function fetchHasHoldings(
  client: HoldingsProbeClient | null | undefined,
  timeoutMs: number = HOLDINGS_PROBE_TIMEOUT_MS,
): Promise<boolean> {
  if (!client) {
    return false;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const res = await Promise.race([
      client.financial.listFinancialRecords(HOLDINGS_QUERY),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error("holdings probe timeout")),
          timeoutMs,
        );
      }),
    ]);
    const rows = extractRows(res);
    return Array.isArray(rows) && rows.length > 0;
  } catch {
    return false;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/**
 * 入口点击用：读空时若刚上传 / 连接成功（{@link HOLDINGS_RECENT_CHANGE_WINDOW_MS} 内）就短重试一次。
 * 超过窗口、从未上传过、或第一次就读到持仓都不会多发请求。不改判定数据源。
 */
export async function fetchHasHoldingsWithRecentRetry(
  client: HoldingsProbeClient | null | undefined,
): Promise<boolean> {
  if (await fetchHasHoldings(client)) {
    return true;
  }
  if (
    lastHoldingsChangeAt === null ||
    Date.now() - lastHoldingsChangeAt > HOLDINGS_RECENT_CHANGE_WINDOW_MS
  ) {
    return false;
  }
  await new Promise((resolve) => setTimeout(resolve, HOLDINGS_RETRY_DELAY_MS));
  return fetchHasHoldings(client);
}

let probeOnce: Promise<boolean> | null = null;
let lastHoldingsChangeAt: number | null = null;

/**
 * 登录场景用：模块级缓存同一个 promise，entry gate 与 `auth_signin_success` 埋点共用，
 * 避免双请求。上传成功 / Plaid 成功后调用 {@link resetHoldingsProbe}。
 */
export function probeHasHoldingsOnce(
  client: HoldingsProbeClient | null | undefined,
): Promise<boolean> {
  if (!client) {
    // client 还没就绪时不缓存：否则一次过早的调用会把「无持仓」钉死到上传 / 绑定成功之前
    return Promise.resolve(false);
  }
  if (!probeOnce) {
    probeOnce = fetchHasHoldings(client);
  }
  return probeOnce;
}

/** 同时记下「持仓刚变过」的时间点，供 {@link fetchHasHoldingsWithRecentRetry} 判断是否在索引延迟窗口内。 */
export function resetHoldingsProbe(): void {
  probeOnce = null;
  lastHoldingsChangeAt = Date.now();
}

/** 仅供单测：清掉「刚变过」的时间点。 */
export function clearHoldingsChangeForTest(): void {
  lastHoldingsChangeAt = null;
}
